import { $getRoot } from 'lexical';
import { useContext, useEffect, useState } from 'react';
import { IMAGES } from '../../constants/images';
import { useAltText } from '../../hooks/useAltText';
import { ImageWithAltText, TextSettings } from '../../types/altText';
import { ImageContext } from '../EditForm/MainEditor';
import { EditorContext } from '../EditForm/EditorContextProvider';
import { $isImageNode, ImageNode } from '../EditForm/nodes/ImageNode';
import ImageThumbnail from './ImageThumbnail';

const AlternativeTextPanel = ({
  postTitle,
  postContent,
  setIsAltTextLoading,
  setIsAltTextSuccess,
}: {
  postTitle: string;
  postContent: string;
  setIsAltTextLoading: (loading: boolean) => void;
  setIsAltTextSuccess: (success: boolean) => void;
}) => {
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState<TextSettings>({
    language: 'Korean',
    tone: 'plain',
    lines: 1,
  });

  const [selectedImage, setSelectedImage] = useState<ImageWithAltText | null>(
    null,
  );
  const [isEditing, setIsEditing] = useState(false);
  const [pendingDeselect, setPendingDeselect] = useState(false);

  const { uploadedImages } = useContext(ImageContext);
  const { editor } = useContext(EditorContext);
  const { images, isLoading, error, updateImages, generateAltTexts } =
    useAltText();

  useEffect(() => {
    const current = images;
    const inEditor = uploadedImages;
    const wasRemoved = current.some(
      (img) => !inEditor.find((u) => u.imageUrl === img.imageUrl),
    );

    const altChanged = inEditor.some((u) => {
      const match = current.find((img) => img.imageUrl === u.imageUrl);
      return !match || match.altText !== u.altText;
    });

    if (wasRemoved || altChanged) {
      const merged = inEditor.map((u) => {
        const prev = current.find((img) => img.imageUrl === u.imageUrl);
        return {
          imageUrl: u.imageUrl,
          altText: prev?.altText ?? u.altText ?? '',
        };
      });
      updateImages(merged);
    }
  }, [uploadedImages, images, updateImages]);

  useEffect(() => {
    if (pendingDeselect) {
      setSelectedImage(null);
      setPendingDeselect(false);
    }
  }, [images, pendingDeselect]);

  const handleSettingChange = (
    key: keyof TextSettings,
    value: string | number,
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleGenerateAltText = async () => {
    if (editor) {
      editor.getEditorState().read(() => {
        postContent = $getRoot().getTextContent();
      });
    }
    try {
      setIsAltTextLoading(true);
      await generateAltTexts(settings, postTitle, postContent);
      if (editor) {
        editor.update(() => {
          $getRoot()
            .getAllDescendants()
            .filter($isImageNode)
            .forEach((node: ImageNode) => {
              const match = images.find((img) => img.imageUrl === node.__src);
              if (match?.altText) {
                node.setAltText(match.altText);
              }
            });
        });
      }
      setIsAltTextSuccess(true);
      setTimeout(() => setIsAltTextSuccess(false), 1000);
    } catch (err) {
      console.error('Alt text generation failed', err);
    } finally {
      setIsAltTextLoading(false);
    }
  };

  const commitChanges = () => {
    if (!selectedImage) return;
    const updated = images.map((img) => ({
      imageUrl: img.imageUrl,
      altText:
        img.imageUrl === selectedImage.imageUrl
          ? (selectedImage.altText ?? '')
          : (img.altText ?? ''),
    }));
    updateImages(updated);

    if (editor) {
      editor.update(() => {
        $getRoot()
          .getAllDescendants()
          .filter($isImageNode)
          .forEach((node: ImageNode) => {
            if (node.__src === selectedImage.imageUrl) {
              node.setAltText(selectedImage.altText ?? '');
            }
          });
      });
    }
  };

  const handleBack = () => {
    if (isEditing) {
      commitChanges();
      setIsEditing(false);
    }
    setSelectedImage(null);
  };

  const imageSlots = [
    ...images,
    ...Array(Math.max(0, 6 - images.length)).fill(null),
  ];

  return (
    <div className="relative flex flex-col max-h-[625px] overflow-y-auto border border-gray-300 w-80 bg-gray-50">
      <div className="flex items-center justify-between px-4 py-4 bg-indigo-100 border-b border-gray-300">
        {/* prev 버튼: selectedImage 있을 때만 */}
        <div className="w-8">
          {selectedImage && (
            <button
              className="p-1 rounded-full hover:bg-gray-200"
              title="뒤로가기"
              onClick={handleBack}
            >
              <img src={IMAGES.prev} alt="뒤로가기" className="w-5 h-5" />
            </button>
          )}
        </div>

        <h2 className="flex-1 text-lg font-semibold text-center">
          대체 텍스트 생성
        </h2>

        {/* 설정 버튼: selectedImage 없을 때만 */}
        {!selectedImage && (
          <div className="relative">
            <button
              className="w-8 p-1 rounded-full hover:bg-gray-200"
              title="설정"
              onClick={() => setShowSettings((v) => !v)}
            >
              <img src={IMAGES.setting} alt="설정" className="w-5 h-5" />
            </button>
            {showSettings && (
              <div className="absolute right-0 z-50 w-64 p-4 mt-1 bg-white border border-gray-300 rounded-md shadow-lg top-full">
                <h3 className="mb-4 font-medium">대체 텍스트 설정</h3>
                <div className="mb-3">
                  <label className="block mb-1 text-sm">문체</label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded"
                    value={settings.tone}
                    onChange={(e) =>
                      handleSettingChange('tone', e.target.value)
                    }
                  >
                    <option value="plain">plain (~사진이다.)</option>
                    <option value="honorific">honorific (~사진입니다.)</option>
                    <option value="informal">informal (~사진.)</option>
                    <option value="polite">polite (~사진이에요.)</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="block mb-1 text-sm">언어</label>
                  <select
                    className="w-full p-2 border border-gray-300 rounded"
                    value={settings.language}
                    onChange={(e) =>
                      handleSettingChange('language', e.target.value)
                    }
                  >
                    <option value="Korean">Korean</option>
                    <option value="English">English</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="block mb-1 text-sm">분량</label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      min={1}
                      max={5}
                      className="w-16 p-2 border border-gray-300 rounded"
                      value={settings.lines}
                      onChange={(e) =>
                        handleSettingChange(
                          'lines',
                          parseInt(e.target.value, 10) || 1,
                        )
                      }
                    />
                    <span className="ml-2">줄 이내</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedImage ? (
        <div className="flex flex-col items-center gap-4 p-4 h-[640px] overflow-y-auto w-full">
          <img
            src={selectedImage.imageUrl}
            alt={selectedImage.altText || '이미지'}
            className="object-contain max-w-full rounded max-h-60"
          />

          <div className="flex justify-end w-full space-x-2">
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} title="수정">
                <img src={IMAGES.edit} alt="수정" className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={() => {
                  commitChanges();
                  setIsEditing(false);
                  setPendingDeselect(true);
                }}
                title="저장"
              >
                <img src={IMAGES.check} alt="저장" className="w-5 h-5" />
              </button>
            )}
          </div>

          {isEditing ? (
            <textarea
              className="w-full p-[5px] border border-[#C7CCD7] rounded text-sm text-gray-700 resize-none"
              rows={3}
              value={selectedImage.altText ?? ''}
              onChange={(e) =>
                setSelectedImage({
                  ...selectedImage,
                  altText: e.target.value,
                })
              }
            />
          ) : (
            <div className="w-full p-[5px] border border-[#C7CCD7] rounded text-sm text-gray-700 whitespace-pre-wrap">
              {selectedImage.altText || '대체 텍스트가 없습니다.'}
            </div>
          )}
        </div>
      ) : (
        <div className="p-4">
          {error && (
            <div className="p-2 mb-4 text-red-500 border border-red-300 rounded bg-red-50">
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {imageSlots.map((image, i) => {
              const latest = image
                ? images.find((img) => img.imageUrl === image.imageUrl)
                : null;

              return (
                <div
                  key={i}
                  className="p-2 bg-white rounded shadow cursor-pointer min-h-[140px] flex flex-col items-center justify-center"
                  onClick={() => image && setSelectedImage(image)}
                >
                  {image ? (
                    <>
                      <ImageThumbnail
                        imageUrl={image.imageUrl}
                        altText={latest?.altText}
                        isLoading={image.isLoading}
                        error={image.error ?? undefined}
                      />
                      {!image.altText && !image.isLoading && (
                        <div className="mt-2 text-xs text-center text-gray-500">
                          생성 대기 중...
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full bg-gray-100 rounded" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-col mt-4">
            <button
              className={`px-6 py-2 rounded-md transition-colors ${
                isLoading
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-[#587FE9] text-white'
              }`}
              onClick={handleGenerateAltText}
              disabled={isLoading || images.length === 0}
            >
              {isLoading ? '생성 중...' : '생성'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlternativeTextPanel;
